// ModalServicio.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Button, MenuItem, Select, FormControl, InputLabel, Box, Typography
} from "@mui/material";
import { fetchWithAuth } from "../components/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "https://api.hmsaa.com.mx:5001";

const shortUuid = (u) => (u ? `${u.slice(0, 8)}…${u.slice(-4)}` : "");

const displayFactura = (f) => {
  const ref = (f?.referencia || "").trim();
  if (ref) return ref;

  const u = (f?.uuid || "").trim();
  if (u) return `UUID ${shortUuid(u)}`;

  return `Factura #${f?.id_factura ?? f?.id ?? "—"}`;
};

export default function ModalServicio({
  open,
  onClose,
  newServicio,
  setNewServicio,
  handleSaveServicio
}) {
  const [proveedores, setProveedores] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [facturaId, setFacturaId] = useState("");

  // valores “seguros” para Select
  const proveedorValue = useMemo(() => String(newServicio.id_proveedor ?? ""), [newServicio.id_proveedor]);
  const conceptoValue = useMemo(() => String(newServicio.xml_concepto_id ?? ""), [newServicio.xml_concepto_id]);
  const facturaValue = useMemo(() => String(facturaId ?? ""), [facturaId]);

  useEffect(() => {
    if (!open) return;

    // carga base
    fetchWithAuth(`${API_URL}/api/facturas/disponibles?tipo=servicio`)
      .then((data) => setFacturas(data || []));
    fetchWithAuth(`${API_URL}/api/proveedores`).then((data) => setProveedores(data || []));

    // reset UI
    setFacturaId("");
    setConceptos([]);
    setNewServicio((prev) => ({
      ...prev,
      xml_concepto_id: "",
      descripcion: prev.descripcion || ""
    }));
  }, [open]);

  useEffect(() => {
    if (!facturaId) {
      setConceptos([]);
      return;
    }

    // ✅ para servicios, filtra conceptos tipo=servicio
    fetchWithAuth(`${API_URL}/api/facturas/${facturaId}/conceptos-disponibles?tipo=servicio`)
      .then((data) => setConceptos(data || []));
  }, [facturaId]);

  const conceptoSeleccionado = useMemo(() => {
    if (!conceptoValue) return null;
    return conceptos.find((x) => String(x.id ?? x.id_xml_concepto) === String(conceptoValue));
  }, [conceptos, conceptoValue]);

  const usaFactura = Boolean(facturaId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Nuevo Servicio</DialogTitle>

      <DialogContent dividers>

        {/* 1) FACTURA primero */}
        <FormControl fullWidth margin="dense">
          <InputLabel id="factura-label">Factura</InputLabel>
          <Select
            labelId="factura-label"
            label="Factura"
            value={facturaValue}
            onChange={(e) => {
              const fid = e.target.value;
              setFacturaId(fid);

              // limpia concepto y descripcion al cambiar factura
              setConceptos([]);
              setNewServicio((prev) => ({
                ...prev,
                xml_concepto_id: "",
                descripcion: ""
              }));
            }}
          >
            {facturas
              .filter((f) => f && (f.id_factura ?? f.id) != null)
              .map((f) => {
                const fid = f.id_factura ?? f.id;
                return (
                  <MenuItem key={`fac-${fid}`} value={String(fid)}>
                    {displayFactura(f)}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>

        {/* 2) CONCEPTO (si hay factura) */}
        <FormControl fullWidth margin="dense" disabled={!usaFactura}>
          <InputLabel id="concepto-label">Concepto XML (servicio)</InputLabel>
          <Select
            labelId="concepto-label"
            label="Concepto XML (servicio)"
            value={conceptoValue}
            onChange={(e) => {
              const selectedId = String(e.target.value);
              const c = conceptos.find((x) => String(x.id ?? x.id_xml_concepto) === selectedId);

              setNewServicio((prev) => ({
                ...prev,
                xml_concepto_id: selectedId,
                // si viene de XML, usamos esa descripción
                descripcion: c?.descripcion || prev.descripcion || ""
              }));
            }}
          >
            {conceptos.length === 0 ? (
              <MenuItem value="" disabled>
                <em>No hay conceptos disponibles</em>
              </MenuItem>
            ) : (
              conceptos
                .filter((c) => c && (c.id ?? c.id_xml_concepto) != null)
                .map((c) => {
                  const cid = c.id ?? c.id_xml_concepto;
                  const disp = c.disponibles ?? "";
                  return (
                    <MenuItem key={`cx-${cid}`} value={String(cid)}>
                      {c.descripcion} {disp !== "" ? ` (Disp: ${disp})` : ""}
                    </MenuItem>
                  );
                })
            )}
          </Select>
        </FormControl>

        {/* hint de precio cuando viene de XML */}
        {usaFactura && conceptoSeleccionado?.valor_unitario != null && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Precio (desde XML): ${Number(conceptoSeleccionado.valor_unitario).toLocaleString("es-MX")}
            </Typography>
          </Box>
        )}

        {/* 3) PROVEEDOR */}
        <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
          <InputLabel id="proveedor-label">Proveedor</InputLabel>
          <Select
            labelId="proveedor-label"
            label="Proveedor"
            value={proveedorValue}
            onChange={(e) =>
              setNewServicio((prev) => ({ ...prev, id_proveedor: e.target.value }))
            }
          >
            {proveedores
              .filter((p) => p && (p.id_proveedor ?? p.id) != null)
              .map((p) => {
                const pid = p.id_proveedor ?? p.id;
                return (
                  <MenuItem key={`prov-${pid}`} value={String(pid)}>
                    {p.razon_social}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>

        {/* 4) NOMBRE */}
        <TextField
          label="Nombre del Servicio"
          fullWidth
          margin="dense"
          sx={{ mt: 2 }}
          value={newServicio.nombre || ""}
          onChange={(e) =>
            setNewServicio((prev) => ({ ...prev, nombre: e.target.value }))
          }
        />

        {/* 5) DESCRIPCIÓN */}
        <TextField
          margin="dense"
          label={usaFactura ? "Descripción (desde XML)" : "Descripción"}
          fullWidth
          multiline
          minRows={2}
          disabled={usaFactura && Boolean(newServicio.xml_concepto_id)}
          value={newServicio.descripcion || ""}
          onChange={(e) =>
            setNewServicio((prev) => ({ ...prev, descripcion: e.target.value }))
          }
        />

        {/* 6) PRECIO si es libre (sin factura) */}
        {!usaFactura && (
          <TextField
            margin="dense"
            label="Precio"
            fullWidth
            type="number"
            value={newServicio.precio || ""}
            onChange={(e) =>
              setNewServicio((prev) => ({ ...prev, precio: e.target.value }))
            }
          />
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSaveServicio}
          disabled={!newServicio.nombre || !newServicio.id_proveedor}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
}
